import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';

import { useIntl } from '@zenbusiness/application-commons-intl';

import { businessTypes } from '#client/constants';

import Display from './Display';
import getActivities from '../helpers/businessActivities';
import { formatAddress, formatContact } from '../helpers/formatData';

const EINCreation = ({
  title = 'Order Details',
  taskData: {
    standalone,
    name,
    type,
    jurisdiction,
    physicalAddress,
    mailingAddress,
    principalAddress,
    reason,
    activity1,
    activity2,
    activity3,
    activity4,
    activity5,
    activitySpecify,
    taxedAsSingleMemberLLC,
    taxedAsSCorp,
    memberCount,
    responsibleParty,
    corpCareOf,
    corpClosingMonth,
    startDate,
    gambling,
    atf,
    employeesWithW2,
    w2WagesPaidDate,
    w2AgriculturalEmployeesCount,
    w2OtherEmployeesCount,
    w2Wageslt1000,
    highwayVehicleOver55000,
    highwayVehicle,
    filesForm720,
    tradeNames
  },
  onClose
}) => {
  const { formatMessage: fm } = useIntl();

  const manuallyLoaded = !activity1;
  const isLLC = type === 'llc';
  const isCorp = type === 'corp';
  const {
    companyName: isResPartyCorp = false,
    legalName,
    ein,
    previousEin,
    alternateLastNames,
    hasSsn: hasSSN,
    collectDataFromUser
  } = responsibleParty || {};

  const sections = [
    {
      title: fm({ id: 'orderView.items.businessName' }),
      filter: standalone,
      items: [{ content: name, xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.formationState' }),
      filter: standalone,
      items: [{ content: jurisdiction, xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.businessType' }),
      filter: standalone,
      items: [{ content: businessTypes[type], xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.addresses' }),
      filter: standalone,
      items: [
        {
          label: fm({ id: 'orderView.items.physicalAddress' }),
          content: physicalAddress ? formatAddress(physicalAddress, fm) : fm({ id: 'orderView.items.none' }),
          xs: 12,
          md: 6
        },
        {
          label: fm({ id: 'orderView.items.mailingAddress' }),
          content: mailingAddress ? formatAddress(mailingAddress, fm) : fm({ id: 'orderView.items.none' }),
          xs: 12,
          md: 6
        },
        {
          label: fm({ id: 'orderView.items.principalAddress' }),
          content: principalAddress ? formatAddress(principalAddress, fm) : fm({ id: 'orderView.items.none' }),
          xs: 12,
          md: 6
        }
      ]
    },
    {
      title: fm({ id: 'orderView.items.einReason' }),
      filter: standalone,
      items: [{ content: reason, xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.corpTaxMethod' }),
      filter: !manuallyLoaded && isCorp,
      items: [{ content: taxedAsSCorp ? fm({ id: 'orderView.items.taxedAsSCorp' }) : fm({ id: 'orderView.items.taxedAsCCorp' }), xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.memberCount' }),
      filter: !manuallyLoaded && isLLC,
      items: [{ content: memberCount, xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.llcTaxMethod' }),
      filter: !manuallyLoaded && isLLC,
      items: [
        { content: taxedAsSingleMemberLLC ? fm({ id: 'orderView.items.singleMemberLlc' }) : fm({ id: 'orderView.items.multiMemberLlc' }), xs: 12 }
      ]
    },
    {
      title: fm({ id: 'orderView.items.responsibleParty' }),
      filter: !manuallyLoaded,
      items: [
        {
          content: collectDataFromUser
            ? fm({ id: 'orderView.items.contactToCollect' })
            : formatContact(responsibleParty, fm),
          xs: 12
        }
      ]
    },
    {
      title: fm({ id: 'orderView.items.manualForms' }),
      filter: !manuallyLoaded && !hasSSN,
      items: [{ content: fm({ id: 'orderView.items.manualFormsDescription' }), xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.responsiblePartyLegalName' }),
      filter: !manuallyLoaded && !collectDataFromUser && isResPartyCorp,
      items: [{ content: legalName, xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.responsiblePartyEin' }),
      filter: !manuallyLoaded && !collectDataFromUser && isResPartyCorp,
      items: [{ content: ein, xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.appliedForEinBefore' }),
      filter: !manuallyLoaded && !collectDataFromUser && !isResPartyCorp,
      items: [{ content: previousEin ? fm({ id: 'orderView.items.yes' }) : fm({ id: 'orderView.items.no' }), xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.responsiblePartyAltNames' }),
      filter: !manuallyLoaded && !collectDataFromUser && !isResPartyCorp,
      items:
        alternateLastNames && alternateLastNames.length > 0
          ? alternateLastNames.map((altLastName) => ({
            content: altLastName,
            xs: 12
          }))
          : [{ content: fm({ id: 'orderView.items.none' }), xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.tradeNames' }),
      items: [{ content: tradeNames, xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.correspondencePerson' }),
      filter: !manuallyLoaded && isCorp,
      items: [{ content: corpCareOf || fm({ id: 'orderView.items.none' }), xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.accountingYearEnd' }),
      filter: !manuallyLoaded && isCorp,
      items: [{ content: corpClosingMonth, xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.corpStartDate' }),
      filter: !manuallyLoaded && standalone,
      items: [{ content: startDate && format(parseISO(startDate), 'MM/dd/yyyy'), xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.gambling' }),
      filter: !manuallyLoaded,
      items: [{ content: gambling ? fm({ id: 'orderView.items.yes' }) : fm({ id: 'orderView.items.no' }), xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.atf' }),
      filter: !manuallyLoaded,
      items: [{ content: atf ? fm({ id: 'orderView.items.yes' }) : fm({ id: 'orderView.items.no' }), xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.employeesW2' }),
      filter: !manuallyLoaded,
      items: [{ content: employeesWithW2 ? fm({ id: 'orderView.items.yes' }) : fm({ id: 'orderView.items.no' }), xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.firstWagesPaidDate' }),
      filter: !manuallyLoaded && employeesWithW2,
      items: [{ content: w2WagesPaidDate, xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.agriculturalEmployees' }),
      filter: !manuallyLoaded && employeesWithW2,
      items: [{ content: w2AgriculturalEmployeesCount, xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.otherEmployees' }),
      filter: !manuallyLoaded && employeesWithW2,
      items: [{ content: w2OtherEmployeesCount, xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.taxLiabilityUnder1000' }),
      filter: !manuallyLoaded && employeesWithW2,
      items: [{ content: w2Wageslt1000 ? fm({ id: 'orderView.items.yes' }) : fm({ id: 'orderView.items.no' }), xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.highwayVehicle' }),
      filter: !manuallyLoaded,
      items: [
        {
          // eslint-disable-next-line no-nested-ternary
          content: highwayVehicle
            ? highwayVehicleOver55000
              ? fm({ id: 'orderView.items.vehicleOver55000' })
              : fm({ id: 'orderView.items.vehicleNotOver55000' })
            : fm({ id: 'orderView.items.no' }),
          xs: 12
        }
      ]
    },
    {
      title: fm({ id: 'orderView.items.requireForm720' }),
      filter: !manuallyLoaded,
      items: [{ content: filesForm720 ? fm({ id: 'orderView.items.yes' }) : fm({ id: 'orderView.items.no' }), xs: 12 }]
    },
    {
      title: fm({ id: 'orderView.items.businessActivity' }),
      filter: !manuallyLoaded,
      items: getActivities(
        activity1,
        activity2,
        activity3,
        activity4,
        activity5,
        activitySpecify
      ).map((activity) => ({
        content: activity,
        xs: 12
      }))
    }
  ];

  return (
    <Display
      onClose={onClose}
      sections={sections}
      title={title}
    />
  );
};

export default EINCreation;

EINCreation.propTypes = {
  title: PropTypes.string,
  taskData: PropTypes.shape({
    standalone: PropTypes.bool,
    name: PropTypes.string,
    type: PropTypes.string,
    jurisdiction: PropTypes.string,
    physicalAddress: PropTypes.shape({}),
    mailingAddress: PropTypes.shape({}),
    principalAddress: PropTypes.shape({}),
    reason: PropTypes.string,
    activity1: PropTypes.string,
    activity2: PropTypes.string,
    activity3: PropTypes.string,
    activity4: PropTypes.string,
    activity5: PropTypes.string,
    activitySpecify: PropTypes.string,
    taxedAsSingleMemberLLC: PropTypes.bool,
    taxedAsSCorp: PropTypes.bool,
    memberCount: PropTypes.number,
    responsibleParty: PropTypes.shape({}),
    collectDataFromUser: PropTypes.bool,
    corpCareOf: PropTypes.string,
    corpClosingMonth: PropTypes.string,
    startDate: PropTypes.string,
    gambling: PropTypes.bool,
    atf: PropTypes.bool,
    employeesWithW2: PropTypes.bool,
    w2WagesPaidDate: PropTypes.string,
    w2AgriculturalEmployeesCount: PropTypes.number,
    w2OtherEmployeesCount: PropTypes.number,
    w2Wageslt1000: PropTypes.bool,
    highwayVehicleOver55000: PropTypes.bool,
    highwayVehicle: PropTypes.bool,
    filesForm720: PropTypes.bool,
    tradeNames: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func.isRequired
};
